import { lazy } from 'react';
import { Box, Text, Group, useMantineTheme, ThemeIcon } from '@mantine/core';
import { IconCurrencyDollar, IconTrendingUp, IconTrendingDown } from '@tabler/icons-react';
import { WidgetProps } from '../../types';
import { registerWidget } from '../../registry';
import { usePreferencesStore } from '../../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../../hooks/useAccessibilityStyles';

// ============================================================================
// RevenueStat Widget Component - Headerless individual stat
// ============================================================================

function RevenueStatWidget({ globalData, instanceId }: WidgetProps) {
  const theme = useMantineTheme();
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const stats = globalData?.invoiceStats;
  const totalRevenue = stats?.paidAmount || 0;

  // Format revenue
  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount.toLocaleString()}`;
  };

  // Mock trend data
  const trend = 15.7;
  const trendUp = trend > 0;

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        textAlign: 'center',
      }}
    >
      <Group gap="xs" mb={4}>
        <ThemeIcon size="md" variant="light" color="green" radius="xl">
          <IconCurrencyDollar size={16} />
        </ThemeIcon>
      </Group>
      <Text size="xl" fw={700} lh={1.2} style={accessibilityStyles}>
        {formatCurrency(totalRevenue)}
      </Text>
      <Text size="xs" c="dimmed" mb={4} style={accessibilityStyles}>
        Total Revenue
      </Text>
      <Group gap={4} justify="center">
        {trendUp ? (
          <IconTrendingUp size={12} color={theme.colors.green[6]} />
        ) : (
          <IconTrendingDown size={12} color={theme.colors.red[6]} />
        )}
        <Text size="xs" c={trendUp ? 'green' : 'red'} fw={500} style={accessibilityStyles}>
          {trendUp ? '+' : ''}{trend}%
        </Text>
      </Group>
    </Box>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'revenue-stat',
  name: 'Revenue',
  description: 'Single metric showing total revenue with trend',
  icon: IconCurrencyDollar,
  category: 'stats',
  component: lazy(() => Promise.resolve({ default: RevenueStatWidget })),
  sizes: {
    base: { cols: 6, rows: 2 },
    sm: { cols: 4, rows: 2 },
    md: { cols: 3, rows: 2 },
    lg: { cols: 2, rows: 2 },
    xl: { cols: 2, rows: 2 },
    tv: { cols: 2, rows: 2 },
  },
  sizeVariants: {
    variants: ['tiny', 'small', 'medium', 'large'],
    defaultVariant: 'small',
    variantSizes: {
      tiny: {
        base: { cols: 4, rows: 1 },
        sm: { cols: 3, rows: 1 },
        md: { cols: 2, rows: 1 },
        lg: { cols: 2, rows: 1 },
        xl: { cols: 2, rows: 1 },
        tv: { cols: 2, rows: 1 },
      },
      small: {
        base: { cols: 6, rows: 2 },
        sm: { cols: 4, rows: 2 },
        md: { cols: 3, rows: 2 },
        lg: { cols: 2, rows: 2 },
        xl: { cols: 2, rows: 2 },
        tv: { cols: 2, rows: 2 },
      },
      medium: {
        base: { cols: 6, rows: 2 },
        sm: { cols: 6, rows: 2 },
        md: { cols: 4, rows: 2 },
        lg: { cols: 3, rows: 2 },
        xl: { cols: 3, rows: 2 },
        tv: { cols: 3, rows: 2 },
      },
      large: {
        base: { cols: 6, rows: 3 },
        sm: { cols: 6, rows: 3 },
        md: { cols: 4, rows: 3 },
        lg: { cols: 3, rows: 3 },
        xl: { cols: 3, rows: 3 },
        tv: { cols: 3, rows: 3 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['invoiceStats'],
  },
  hasHeader: false,
  displayStyle: 'stat',
  hasSettings: true,
  collapsible: false,
  expandable: false,
});

export default RevenueStatWidget;
